import { EpicWithStatus } from '../interfaces/epic.interface'
import { WorkItem } from '../interfaces/worklog.interface'

/**
 * Un bloque del Backlog: una épica con sus tareas, o una tarea suelta.
 *
 * El Backlog se ordena por bloques y no por tareas porque **la épica es la unidad que se prioriza**:
 * si una épica entra al sprint, entra entera, y el orden interno de sus tareas no decide nada — eso
 * se resuelve en el Tablero.
 */
export interface BacklogGroup {
  kind: 'epic' | 'loose'
  /** Slug de la épica, o el id de la tarea cuando va suelta. Es la llave del plegado. */
  key: string
  title: string
  items: WorkItem[]
  /** Posición del bloque: la de su primera tarea. */
  rank: number
  /** Puntos de las tareas que están AQUÍ, que es lo que se está priorizando. */
  pendingPoints: number
  /** Puntos y tareas de la épica completa, para dar la escala. `null` en una tarea suelta. */
  totalPoints: number | null
  totalTasks: number | null
  doneTasks: number | null
  statusColor: string | null
  statusLabel: string | null
}

/** Nombre del bloque que recoge lo que no pertenece a ninguna épica. */
export const SIN_EPICA = 'Sin épica'

/** La épica de una tarea, si viene poblada. El backlog la pide con `select: 'slug title'`. */
function epicaDe(it: WorkItem): { _id?: string; slug?: string; title?: string } | null {
  const e: any = (it as any).epic
  return e && typeof e === 'object' ? e : null
}

/**
 * Agrupa las tareas de una cubeta en bloques ordenados.
 *
 * Las tareas llegan ya ordenadas por `rank`, así que el orden de aparición de cada épica fija el
 * suyo: el bloque vale lo que vale su primera tarea. Las que no tienen épica NO se juntan en un
 * cajón al fondo — cada una es su propio bloque y compite en el mismo orden que las épicas. Son el
 * 38 % del backlog: enterrarlas en un grupo colapsado las volvería invisibles.
 */
export function buildBacklogGroups(items: WorkItem[], epics: EpicWithStatus[] = [], statusColor: (s: string) => string | null = () => null): BacklogGroup[] {
  const porSlug = new Map<string, EpicWithStatus>()
  for (const e of epics) porSlug.set(e.epic.slug, e)

  const grupos: BacklogGroup[] = []
  const indice = new Map<string, BacklogGroup>()

  for (const it of items) {
    const epica = epicaDe(it)
    const puntos = it.points ?? 0

    if (!epica?.slug) {
      grupos.push({
        kind: 'loose',
        key: it._id,
        title: it.title,
        items: [it],
        rank: it.rank ?? 0,
        pendingPoints: puntos,
        totalPoints: null,
        totalTasks: null,
        doneTasks: null,
        statusColor: null,
        statusLabel: null,
      })
      continue
    }

    const yaEsta = indice.get(epica.slug)
    if (yaEsta) {
      yaEsta.items.push(it)
      yaEsta.pendingPoints += puntos
      continue
    }

    const meta = porSlug.get(epica.slug)
    const grupo: BacklogGroup = {
      kind: 'epic',
      key: epica.slug,
      title: epica.title || epica.slug,
      items: [it],
      //* La posición del bloque es la de su PRIMERA tarea: como llegan ordenadas, es la menor.
      rank: it.rank ?? 0,
      pendingPoints: puntos,
      totalPoints: meta?.points ?? null,
      totalTasks: meta?.taskCount ?? null,
      doneTasks: meta?.doneCount ?? null,
      statusColor: meta ? statusColor(meta.status) : null,
      statusLabel: meta?.status ?? null,
    }
    indice.set(epica.slug, grupo)
    grupos.push(grupo)
  }

  return grupos
}

/** Aplana los bloques al orden real de tareas. Es lo que se manda al servidor. */
export function flattenGroups(groups: BacklogGroup[]): WorkItem[] {
  return groups.flatMap((g) => g.items)
}

//* Hueco por defecto entre dos posiciones. El mismo que usa el reordenamiento de una sola tarea.
export const RANK_STEP = 1000

/**
 * Reparte posiciones para las `count` tareas de un bloque entre dos vecinos.
 *
 * Es lo que permite mover una épica entera conservando el orden interno: se calcula el hueco
 * disponible y se reparte a partes iguales. Devuelve `null` cuando no cabe — dos vecinos con la
 * misma posición, o tan juntos que el reparto daría números indistinguibles. Ese caso ya tiene su
 * aviso en pantalla («posiciones empatadas») y se resuelve recargando.
 *
 * @param prev posición del último elemento que queda ARRIBA, o null si el bloque va al principio
 * @param next posición del primer elemento que queda ABAJO, o null si va al final
 */
export function spreadRanks(prev: number | null, next: number | null, count: number): number[] | null {
  if (count <= 0) return []

  //* Sin vecinos por algún lado, se inventa el espacio: hay sitio de sobra.
  const desde = prev ?? (next ?? 0) - RANK_STEP * (count + 1)
  const hasta = next ?? (prev ?? 0) + RANK_STEP * (count + 1)

  const hueco = (hasta - desde) / (count + 1)
  //! Sin margen suficiente los números saldrían iguales entre sí o iguales a un vecino, y el orden
  //! dejaría de ser determinista: Mongo devolvería lo que le viniera en gana y la lista bailaría.
  if (!Number.isFinite(hueco) || hueco <= 0.000001) return null

  return Array.from({ length: count }, (_, i) => desde + hueco * (i + 1))
}
